import React, { useState } from 'react';
import { HeadphonesIcon, CalendarIcon, ClockIcon, BookIcon, FilterIcon, SearchIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
import { useDarkClasses } from '../../hooks/useDarkClasses';
const narrationOpportunities = [{
  id: '1',
  title: 'Fantasy Series Narration',
  description: 'Looking for a narrator with a rich, engaging voice for a 3-book epic fantasy series. Multiple character voices required.',
  deadline: '2023-12-15',
  postedDate: '2023-10-01',
  publisherName: 'Epic Realms Publishing',
  publisherLogo: 'https://images.unsplash.com/photo-1560415755-bd80d06eda60?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
  genres: ['Fantasy', 'Epic'],
  wordCount: '95,000 words per book',
  projectLength: 'Long-term (6+ months)',
  compensation: 'Per Finished Hour (PFH)',
  rate: '$200-300 PFH',
  requirements: 'Experience with fantasy narration and character voices. Must have professional home studio.'
}, {
  id: '2',
  title: 'Romance Novel Collection',
  description: 'Seeking a female narrator with a warm, inviting voice for a collection of contemporary romance novels.',
  deadline: '2023-11-30',
  postedDate: '2023-09-15',
  publisherName: 'Heart & Soul Books',
  publisherLogo: 'https://images.unsplash.com/photo-1516383607781-913a19294fd1?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
  genres: ['Romance', 'Contemporary'],
  wordCount: '75,000 words per book',
  projectLength: 'Medium-term (3-6 months)',
  compensation: 'Royalty Share Plus',
  rate: '$100 PFH + Royalty Share',
  requirements: 'Female voice with emotional range. Experience with romance narration preferred.'
}, {
  id: '3',
  title: 'Business Leadership Audiobook',
  description: 'Professional narrator needed for a non-fiction business leadership book. Clear, authoritative voice required.',
  deadline: '2023-12-30',
  postedDate: '2023-10-10',
  publisherName: 'Executive Press',
  publisherLogo: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
  genres: ['Non-Fiction', 'Business', 'Leadership'],
  wordCount: '65,000 words',
  projectLength: 'Short-term (< 1 month)',
  compensation: 'Per Finished Hour (PFH)',
  rate: '$350-400 PFH',
  requirements: 'Professional voice with clarity and authority. Experience with business/non-fiction titles.'
}, {
  id: '4',
  title: 'YA Science Fiction Series',
  description: 'Voice actor needed for a young adult sci-fi series with diverse characters and futuristic settings.',
  deadline: '2024-01-15',
  postedDate: '2023-10-05',
  publisherName: 'Future Horizons Publishing',
  publisherLogo: 'https://images.unsplash.com/photo-1614332287897-cdc485fa562d?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
  genres: ['Science Fiction', 'Young Adult'],
  wordCount: '80,000 words per book',
  projectLength: 'Medium-term (3-6 months)',
  compensation: 'Royalty Share',
  rate: 'Royalty Share',
  requirements: 'Youthful voice with range for multiple character voices. Experience with sci-fi terminology preferred.'
}, {
  id: '5',
  title: 'Historical Mystery Audiobook',
  description: 'Seeking a narrator for a historical mystery set in 1920s London. British accent required.',
  deadline: '2023-12-10',
  postedDate: '2023-09-25',
  publisherName: 'Vintage Mystery Press',
  publisherLogo: 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
  genres: ['Mystery', 'Historical Fiction'],
  wordCount: '90,000 words',
  projectLength: 'Short-term (< 1 month)',
  compensation: 'Per Finished Hour (PFH)',
  rate: '$250-300 PFH',
  requirements: 'Authentic British accent. Experience with period pieces and mystery narration.'
}];
type ProjectLength = 'Any' | 'Short-term (< 1 month)' | 'Medium-term (3-6 months)' | 'Long-term (6+ months)';
type CompensationType = 'Any' | 'Per Finished Hour (PFH)' | 'Royalty Share' | 'Royalty Share Plus';
const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString(undefined, {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
});
const getDaysRemaining = (deadline: string) => {
  const diff = new Date(deadline).getTime() - new Date().getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};
const NarrationOpportunities = () => {
  const dk = useDarkClasses();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [projectLength, setProjectLength] = useState<ProjectLength>('Any');
  const [compensationType, setCompensationType] = useState<CompensationType>('Any');
  const [showFilters, setShowFilters] = useState(false);
  const allGenres = Array.from(new Set(narrationOpportunities.flatMap(o => o.genres)));
  const filtered = narrationOpportunities.filter(o => {
    const matchesSearch = searchTerm === '' || o.title.toLowerCase().includes(searchTerm.toLowerCase()) || o.description.toLowerCase().includes(searchTerm.toLowerCase()) || o.publisherName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGenres = selectedGenres.length === 0 || selectedGenres.some(g => o.genres.includes(g));
    const matchesLength = projectLength === 'Any' || o.projectLength === projectLength;
    const matchesComp = compensationType === 'Any' || o.compensation === compensationType;
    return matchesSearch && matchesGenres && matchesLength && matchesComp;
  });
  const toggleGenre = (genre: string) => setSelectedGenres(prev => prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]);
  const selectClass = `w-full border rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-amber-500 ${dk.input}`;
  return <div className={`min-h-screen ${dk.page}`} data-id="element-12026" data-mp="38x14ixd0gwt1">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-12027" data-mp="33x6dmtt8k77z">
        <div className="mb-8" data-id="element-12028" data-mp="3it453ys7sndv">
          <div className="mb-4" data-id="element-12029" data-mp="3iu5kgfg9jw1e">
            <BackButton data-id="element-12030" data-mp="3irazmmtc897g" />
          </div>
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-12031" data-mp="3dtrjfhd2xmcv">
            Narration Opportunities
          </h1>
          <p className={`mt-2 ${dk.textSecondary}`} data-id="element-12032" data-mp="3rt0rtuv1nc6a">
            Find audiobook narration projects from publishers and authors
          </p>
        </div>

        {/* Search and Filters */}
        <div className={`rounded-lg shadow-md p-6 mb-8 border ${dk.card}`} data-id="element-12033" data-mp="5er6jumeuk9q">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4" data-id="element-12034" data-mp="fobn3059ufh">
            <div className="relative flex-1" data-id="element-12035" data-mp="eewafuseb0j5">
              <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textFaint}`} data-id="element-12036" data-mp="efp8m7daghpo" />
              <input type="text" placeholder="Search by title, description, or publisher..." className={`pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 ${dk.inputOnCard}`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-12037" data-mp="t1v13njfhqwt" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700" data-id="element-12038" data-mp="abdb3qpzd9f5">
              <FilterIcon className="h-4 w-4" data-id="element-12039" data-mp="1aowouwod71q" />
              Filters {showFilters ? '(Hide)' : '(Show)'}
            </button>
          </div>

          {showFilters && <div className={`border-t pt-4 space-y-4 ${dk.border}`} data-id="element-12040" data-mp="5cjuhpfed2ar">
              <div data-id="element-12041" data-mp="3niy8tphkve9o">
                <h3 className={`text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-12042" data-mp="3nieyatu9bs5o">
                  Filter by Genre
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-12043" data-mp="btmzwxvdkkmh">
                  {allGenres.map(genre => <button key={genre} onClick={() => toggleGenre(genre)} className={`px-3 py-1 rounded-full text-sm border transition-all ${selectedGenres.includes(genre) ? 'bg-amber-100 text-amber-800 border-amber-300' : `${dk.badgeNeutral} ${dk.border}`}`} data-id="element-12044" data-mp="6uiqq78gamw5">
                      {genre}
                    </button>)}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-12045" data-mp="3k83xzcroxeft">
                <div data-id="element-12046" data-mp="3fb58j7iz1vny">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-12047" data-mp="3falnvj1xst68">
                    Project Length
                  </label>
                  <select value={projectLength} onChange={e => setProjectLength(e.target.value as ProjectLength)} className={selectClass} data-id="element-12048" data-mp="3694eskmy5rr1">
                    <option value="Any" data-id="element-12049" data-mp="1lqtugopptvnr">Any Length</option>
                    <option value="Short-term (< 1 month)" data-id="element-12050" data-mp="1ltg18m4s874x">
                      Short-term (&lt; 1 month)
                    </option>
                    <option value="Medium-term (3-6 months)" data-id="element-12051" data-mp="1ust8r99ciln0">
                      Medium-term (3-6 months)
                    </option>
                    <option value="Long-term (6+ months)" data-id="element-12052" data-mp="y3lm1xwzq6hi">
                      Long-term (6+ months)
                    </option>
                  </select>
                </div>
                <div data-id="element-12053" data-mp="171nu2xf55zea">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-12054" data-mp="171fir9yo3257">
                    Compensation Type
                  </label>
                  <select value={compensationType} onChange={e => setCompensationType(e.target.value as CompensationType)} className={selectClass} data-id="element-12055" data-mp="fb19jr1lmdzv">
                    <option value="Any" data-id="element-12056" data-mp="fwuv2b91aako">Any Type</option>
                    <option value="Per Finished Hour (PFH)" data-id="element-12057" data-mp="fzqr1u1bmp6h">
                      Per Finished Hour (PFH)
                    </option>
                    <option value="Royalty Share" data-id="element-12058" data-mp="oyuefa7itqqs">Royalty Share</option>
                    <option value="Royalty Share Plus" data-id="element-12059" data-mp="3odplgr8na2hv">
                      Royalty Share Plus
                    </option>
                  </select>
                </div>
              </div>
            </div>}
        </div>

        {/* Opportunities */}
        <div className="space-y-6" data-id="element-12060" data-mp="17p04zh8b6il">
          {filtered.map(o => {
          const days = getDaysRemaining(o.deadline);
          const urgencyClass = days <= 7 ? 'bg-red-100 text-red-800' : days <= 14 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800';
          return <div key={o.id} className={`rounded-lg shadow-md overflow-hidden border ${dk.card}`} data-id="element-12061" data-mp="323zfpt2d242s">
                <div className="p-6" data-id="element-12062" data-mp="2s74h7a6383oi">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4" data-id="element-12063" data-mp="2s635d3h8zd5q">
                    <div data-id="element-12064" data-mp="2o4hnt4trqap7">
                      <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-12065" data-mp="2o59j0ejge6ey">
                        {o.title}
                      </h2>
                      <div className="flex items-center mt-2 gap-2" data-id="element-12066" data-mp="2ef7s7ezpu9wc">
                        <img src={o.publisherLogo} alt={o.publisherName} className="h-7 w-7 rounded-full object-cover" data-id="element-12067" data-mp="2edwoff69t258" />
                        <span className={`text-sm ${dk.textSecondary}`} data-id="element-12068" data-mp="20fovrjg9omzv">
                          {o.publisherName}
                        </span>
                        <span className={dk.textFaint} data-id="element-12069" data-mp="1vhdlj7m28ah4">•</span>
                        <span className={`text-sm flex items-center gap-1 ${dk.textMuted}`} data-id="element-12070" data-mp="1lsq4pplbnulo">
                          <CalendarIcon className="h-3.5 w-3.5" data-id="element-12071" data-mp="1pw642tos0922" />
                          Posted {formatDate(o.postedDate)}
                        </span>
                      </div>
                    </div>
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${urgencyClass}`} data-id="element-12072" data-mp="1gw98e83yy9xi">
                      <ClockIcon className="h-4 w-4" data-id="element-12073" data-mp="29iarykoide0r" />
                      {days} days left
                    </div>
                  </div>

                  <p className={`mb-6 ${dk.textSecondary}`} data-id="element-12074" data-mp="1zt0yb0e0nnaj">{o.description}</p>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-12075" data-mp="1zqxxrmf8mbrk">
                    {[{
                  label: 'WORD COUNT',
                  value: o.wordCount,
                  icon: <BookIcon className="h-4 w-4 text-amber-500 mr-1" data-id="element-12076" data-mp="gtb5z3uqyptv" />
                }, {
                  label: 'PROJECT LENGTH',
                  value: o.projectLength,
                  icon: <CalendarIcon className="h-4 w-4 text-amber-500 mr-1" data-id="element-12077" data-mp="lkjhc5fn0l9o" />
                }, {
                  label: 'COMPENSATION',
                  value: o.compensation,
                  icon: null
                }, {
                  label: 'RATE',
                  value: o.rate,
                  icon: null
                }].map(({
                  label,
                  value,
                  icon
                }) => <div key={label} data-id="element-12078" data-mp="1ia0u14d1f7kv">
                        <h3 className={`text-xs font-medium mb-1 ${dk.textFaint}`} data-id="element-12079" data-mp="1id58cuk7vjp9">
                          {label}
                        </h3>
                        <p className={`text-sm flex items-center ${dk.textPrimary}`} data-id="element-12080" data-mp="28os5b1f6280d">
                          {icon}
                          {value}
                        </p>
                      </div>)}
                  </div>

                  <div className="mb-6" data-id="element-12081" data-mp="2rl9rl3b8xfp8">
                    <h3 className={`text-xs font-medium mb-2 ${dk.textFaint}`} data-id="element-12082" data-mp="2rk8gwzy56v81">
                      GENRES
                    </h3>
                    <div className="flex flex-wrap gap-1.5" data-id="element-12083" data-mp="2wijmgyhemjkg">
                      {o.genres.map(genre => <span key={genre} className={`text-xs px-2 py-0.5 rounded-full ${dk.badgeNeutral}`} data-id="element-12084" data-mp="31dfisatobt3d">
                          {genre}
                        </span>)}
                    </div>
                  </div>

                  <div className={`border-t pt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 ${dk.border}`} data-id="element-12085" data-mp="13n931yulu68t">
                    <div data-id="element-12086" data-mp="zo9ywggp6dif">
                      <h3 className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-12087" data-mp="umkon31au3ve">
                        Requirements
                      </h3>
                      <p className={`text-sm ${dk.textSecondary}`} data-id="element-12088" data-mp="ppamqnwq3zqo">
                        {o.requirements}
                      </p>
                    </div>
                    <button className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors" data-id="element-12089" data-mp="1ibvf9roxqhvt">
                      Apply Now
                    </button>
                  </div>
                </div>
              </div>;
        })}

          {filtered.length === 0 && <div className={`text-center py-12 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-12090" data-mp="263am9xrnpnde">
              <HeadphonesIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textFaint}`} data-id="element-12091" data-mp="3vj3d7dffnmli" />
              <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-12092" data-mp="3fu517rxbyen">
                No opportunities found
              </h3>
              <p className={dk.textMuted} data-id="element-12093" data-mp="3lsikg6mfxcai">
                Try adjusting your search or filters
              </p>
            </div>}
        </div>
      </div>
    </div>;
};
export default NarrationOpportunities;